import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { view, utils, i18next, cssGenerator } from '@yola/ws-sdk';
import bowser from 'yola-bowser';
import highlighter from 'src/js/modules/highlighter';
import useHandlePosition from 'src/js/modules/extensions/common/hooks/use-handle-position';
import calculateOffsetTop from 'src/js/modules/extensions/common/helpers/calculate-offset-top';
import useDrag from 'src/js/modules/extensions/common/hooks/use-drag';
import ResizeHandle from 'src/js/modules/extensions/common/components/resize-handle';
import resizeToolPosition from 'src/js/modules/extensions/common/constants/resize-handle-positions';
import useEventListener from 'src/js/modules/utils/custom-hooks/use-event-listener';
import ResizeTooltip from 'src/js/modules/extensions/registry/ws-image/components/resize-tooltip';
import useMatchBreakpoint from 'src/js/modules/utils/custom-hooks/use-match-breakpoint';
import getDefaultTraits from 'src/js/modules/analytics/segment/helpers/get-default-traits';
import highlighterOffset from 'src/js/modules/highlighter/helpers/highlighter-offset';
import segment from 'src/js/modules/analytics/segment';
import GridContainer from 'src/js/modules/extensions/registry/ws-column/containers/grid-container';
import getColumnsAlignment from '../helpers/get-column-alignment';
import { MIN_COLUMN_WIDTH, FLEX_BASIS_PROPERTY, MAX_WIDTH_PROPERTY } from '../constants';

const {
  track,
  constants: { events },
} = segment;
const { COLUMN_WIDTH } = cssGenerator.constants.attributes;

const getCaptions = () => {
  const isMac = bowser.macOS || bowser.iOS;
  const captions = {
    cmdPressed: i18next.t('Release <b>Ctrl</b> to stick to the grid'),
    cmdReleased: i18next.t('Hold <b>Ctrl</b> for free resizing'),
    shiftPressed: i18next.t('Release <b>Shift</b> to resize this column only'),
    shiftReleased: i18next.t('Hold <b>Shift</b> to resize all columns'),
  };

  if (isMac) {
    captions.cmdPressed = i18next.t('Release <b>Cmd</b> to stick to the grid');
    captions.cmdReleased = i18next.t('Hold <b>Cmd</b> for free resizing');
  }

  return captions;
};

const DESKTOP_BREAKPOINT = '992px';
const COLUMNS_COUNT = 12;

const isCommandPressed = (event) => {
  const { ctrlKey, metaKey } = event;
  if (bowser.macOS) {
    return metaKey;
  }
  return ctrlKey;
};

const getGridWidth = (currentWidth, columnWidth, columnGap) => {
  const columnIndex = Math.round(currentWidth / (columnWidth + columnGap));
  const clampedIndex = Math.max(1, Math.min(COLUMNS_COUNT, columnIndex));
  return clampedIndex * columnWidth + clampedIndex * columnGap;
};

const snapToGridWidth = (currentWidth, relativeOffset, columnWidth, columnGap) => {
  const offsetColumnIndex = Math.ceil(relativeOffset / (columnWidth + columnGap));
  const offsetClampedColumnWidth = offsetColumnIndex * columnWidth + offsetColumnIndex * columnGap;
  // Offset is the difference between the custom & predefined columns width
  const offset = offsetClampedColumnWidth - relativeOffset;

  const columnIndex = Math.round((currentWidth - offset) / (columnWidth + columnGap));
  const clampedColumnWidth = columnIndex * columnWidth + columnIndex * columnGap + offset;

  if (Math.abs(currentWidth - clampedColumnWidth) < 10) {
    return clampedColumnWidth;
  }

  return currentWidth;
};

const ColumnResizeTool = ({
  elementId,
  appearanceStyle,
  onActionStart,
  onActionEnd,
  onMouseEnter = utils.noop,
  onMouseLeave = utils.noop,
}) => {
  const element = useMemo(() => view.accessors.getLiveElement(elementId), [elementId]);
  const liveDoc = view.accessors.getLiveDocument();
  const columnPadding = useMemo(() => {
    const computedStyle = liveDoc.defaultView.getComputedStyle(element);
    return parseInt(computedStyle.paddingLeft, 10);
  }, [element]);
  const columnContainer = useMemo(() => element.parentElement, [element]);
  const columnContainerBounds = useMemo(
    () => columnContainer.getBoundingClientRect(),
    [columnContainer]
  );
  const columnContainerAlignment = useMemo(
    () => getColumnsAlignment(columnContainer),
    [columnContainer]
  );
  const isFillEnabled = useMemo(
    () => columnContainer.getAttribute('width') === 'fill',
    [columnContainer]
  );
  const allColumns = useMemo(() => Array.from(columnContainer.children), [columnContainer]);
  const multiElementResizeAvailable = useMemo(() => allColumns.length > 1, [allColumns]);
  const [shiftPressed, setShiftStatus] = useState(false);
  const [commandPressed, setCommandStatus] = useState(false);
  const [tooltipPosition, showTooltip] = useState(null);
  const [isResizing, setResizingStatus] = useState(false);
  const leftHandleRef = useRef(null);
  const rightHandleRef = useRef(null);
  const currentRect = useRef(null);
  const activeDragHandlerPos = useRef(null);
  const columnWidth = useRef(null);
  const lastDistance = useRef([0, 0]);
  const scrollPosition = useSelector(view.selectors.getScrollPosition);
  const deviceMode = useSelector(view.selectors.getDeviceMode);
  const indent = highlighterOffset.get();
  const isDesktopView = useMatchBreakpoint(DESKTOP_BREAKPOINT);
  const captions = useMemo(getCaptions, []);

  const positions = useMemo(() => {
    switch (columnContainerAlignment) {
      case 'center':
        return {
          [resizeToolPosition.CENTER_LEFT]: {
            correctionX: columnPadding - indent,
          },
          [resizeToolPosition.CENTER_RIGHT]: {
            correctionX: -columnPadding + indent,
          },
        };
      case 'right':
        return {
          [resizeToolPosition.CENTER_LEFT]: {
            correctionX: columnPadding - indent,
          },
        };

      default:
        return {
          [resizeToolPosition.CENTER_RIGHT]: {
            correctionX: -columnPadding + indent,
          },
        };
    }
  }, [columnPadding, columnContainerAlignment, indent]);

  const [handlePosition, updateHandlePosition] = useHandlePosition({
    element,
    positions,
  });

  const showHighlighter = useCallback(
    (options = {}) => {
      const { withElementSize = false } = options;
      highlighter.operations.show([element], {
        forceUpdate: true,
        labelGap: 'medium',
        withElementSize,
      });
    },
    [element]
  );

  useEffect(() => {
    if (isDesktopView) {
      highlighter.operations.hide();
      showHighlighter();
    }

    return () => {
      highlighter.operations.hide();
    };
  }, [showHighlighter, isDesktopView]);

  const calculateNewWidth = useCallback(
    (distance) => {
      const {
        width: containerWidth,
        left: containerLeft,
        right: containerRight,
      } = columnContainerBounds;
      const { width: currentWidth, left: currentLeft, right: currentRight } = currentRect.current;
      let distanceWidth;
      let relativeOffset;

      switch (columnContainerAlignment) {
        case 'center':
          if (activeDragHandlerPos.current === 'left') {
            distanceWidth = -distance[0] * 2;
          } else {
            distanceWidth = distance[0] * 2;
          }
          break;
        case 'right':
          distanceWidth = -distance[0];
          relativeOffset = containerRight - currentRight;
          break;
        default:
          [distanceWidth] = distance;
          relativeOffset = currentLeft - containerLeft;
      }

      const newAbsoluteWidth = currentWidth + distanceWidth;
      let width = Math.max(MIN_COLUMN_WIDTH, Math.min(newAbsoluteWidth, containerWidth));

      if (!commandPressed) {
        width = getGridWidth(width, columnWidth.current, columnPadding * 2);
      } else if (relativeOffset !== null) {
        width = snapToGridWidth(width, relativeOffset, columnWidth.current, columnPadding * 2);
      }

      return ((width / containerWidth) * 100).toFixed(4);
    },
    [columnContainerAlignment, commandPressed, columnContainerBounds, columnPadding]
  );

  const handleDrag = useCallback(
    (distance) => {
      const width = calculateNewWidth(distance);
      const elements = shiftPressed ? allColumns : [element];

      elements.forEach((column) => {
        column.style.setProperty(FLEX_BASIS_PROPERTY, `${width}%`);
        column.style.setProperty(MAX_WIDTH_PROPERTY, `${width}%`);
      });

      updateHandlePosition();
      lastDistance.current = distance;
    },
    [allColumns, element, shiftPressed, updateHandlePosition, calculateNewWidth]
  );

  const handleDragEnd = (distance) => {
    const width = calculateNewWidth(distance);

    let operations = [];

    if (shiftPressed) {
      const newOperations = Array.from(allColumns).map((column) => {
        const elId = view.accessors.getLiveElementId(column);
        return [view.operations.setResponsiveAttribute, [elId, COLUMN_WIDTH, `${width}%`, 'lg']];
      });

      operations = operations.concat(newOperations);
    } else {
      operations.push([
        view.operations.setResponsiveAttribute,
        [elementId, COLUMN_WIDTH, `${width}%`, 'lg'],
      ]);
    }

    if (isFillEnabled) {
      const columnElementId = view.accessors.getLiveElementId(columnContainer);
      operations.push([view.operations.removeElementAttribute, [columnElementId, 'width']]);
    }

    view.operations.bulkViewOperations(operations);

    allColumns.forEach((column) => {
      column.style.removeProperty(MAX_WIDTH_PROPERTY);
      column.style.removeProperty(FLEX_BASIS_PROPERTY);
    });

    const oldWidth = Math.round(currentRect.current.width);
    const newWidth = Math.round(Math.round((width * columnContainerBounds.width) / 100));

    return {
      newWidth: `${newWidth}px`,
      oldWidth: `${oldWidth}px`,
    };
  };

  const resetColumnsWidth = useCallback(() => {
    allColumns.forEach((column) => {
      if (column === element) return;
      column.style.removeProperty(FLEX_BASIS_PROPERTY);
      column.style.removeProperty(MAX_WIDTH_PROPERTY);
    });
  }, [allColumns, element]);

  const handleMouseEnter = (e) => {
    const { target } = e;
    onMouseEnter();

    let position = resizeToolPosition.CENTER_RIGHT;
    if (target === leftHandleRef.current) {
      position = resizeToolPosition.CENTER_LEFT;
    }

    if (!isResizing) {
      showTooltip(position);
    }
  };

  const handleMouseLeave = () => {
    onMouseLeave();
    if (!isResizing) {
      showTooltip(null);
    }
  };

  useDrag(
    [leftHandleRef, rightHandleRef],
    {
      onStart(event) {
        activeDragHandlerPos.current = event.target === leftHandleRef.current ? 'left' : 'right';
        showHighlighter({ isGroup: shiftPressed, withElementSize: true });
        currentRect.current = element.getBoundingClientRect();
        columnWidth.current = columnContainerBounds.width / COLUMNS_COUNT - columnPadding * 2;
        setResizingStatus(true);
        showTooltip(null);
        if (isFillEnabled) {
          columnContainer.removeAttribute('width');
        }

        onActionStart();

        track(events.COLUMN_RESIZING_INITIATED, {
          ...getDefaultTraits(elementId),
        });
      },

      onMove(distance) {
        handleDrag(distance);
        showHighlighter({ withElementSize: true });
      },

      onEnd(distance) {
        setResizingStatus(false);
        const { newWidth, oldWidth } = handleDragEnd(distance, true);

        onActionEnd();
        if (shiftPressed) {
          const otherColumns = allColumns.filter((column) => column !== element);
          highlighter.operations.hide(otherColumns);
        }
        track(events.COLUMN_RESIZING_COMPLETED, {
          ...getDefaultTraits(elementId),
          affectedColumns: shiftPressed ? allColumns.length : 1,
          newWidth,
          oldWidth,
          shiftKeyPressed: shiftPressed,
        });
      },
    },
    { throttleMove: true }
  );

  useEffect(() => {
    if (isResizing) {
      handleDrag(lastDistance.current);

      if (shiftPressed) {
        highlighter.operations.show(allColumns, {
          isGroup: true,
          withElementSize: true,
        });
      } else {
        resetColumnsWidth();
        const otherColumns = allColumns.filter((column) => column !== element);
        highlighter.operations.hide(otherColumns);
        updateHandlePosition();
      }
    }
  }, [
    shiftPressed,
    commandPressed,
    isResizing,
    handleDrag,
    resetColumnsWidth,
    updateHandlePosition,
    allColumns,
    element,
  ]);

  const handleKey = ({ shiftKey, ctrlKey, metaKey }) => {
    const cmdPressed = isCommandPressed({ ctrlKey, metaKey });
    const shouldUpdate = cmdPressed !== commandPressed || shiftKey !== shiftPressed;
    if (!shouldUpdate) return;

    setShiftStatus(shiftKey);
    setCommandStatus(cmdPressed);
  };

  const keyEventCallback = multiElementResizeAvailable ? handleKey : utils.noop;

  useEventListener('keydown', keyEventCallback, document);
  useEventListener('keyup', keyEventCallback, document);
  useEventListener('keydown', keyEventCallback, element.ownerDocument);
  useEventListener('keyup', keyEventCallback, element.ownerDocument);

  if (
    !isDesktopView ||
    deviceMode !== 'desktop' ||
    (!handlePosition[resizeToolPosition.CENTER_LEFT] &&
      !handlePosition[resizeToolPosition.CENTER_RIGHT])
  ) {
    return null;
  }

  const leftPosition = handlePosition[resizeToolPosition.CENTER_LEFT];
  const rightPosition = handlePosition[resizeToolPosition.CENTER_RIGHT];
  const pos = leftPosition || rightPosition;

  const offtop = calculateOffsetTop(element, pos[1]);

  return (
    <React.Fragment>
      {columnContainerAlignment !== 'left' && (
        <ResizeHandle
          ref={leftHandleRef}
          left={leftPosition[0]}
          top={offtop}
          shape="oval"
          direction="left-right"
          appearanceStyle={appearanceStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}
      {columnContainerAlignment !== 'right' && (
        <ResizeHandle
          ref={rightHandleRef}
          left={rightPosition[0]}
          top={offtop}
          shape="oval"
          direction="left-right"
          appearanceStyle={appearanceStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}

      {isResizing && (
        <GridContainer
          scrollPosition={scrollPosition}
          containerBounds={columnContainerBounds}
          columnGap={columnPadding * 2}
          columnsCount={COLUMNS_COUNT}
        />
      )}

      {tooltipPosition && (
        <ResizeTooltip
          left={handlePosition[tooltipPosition][0]}
          top={handlePosition[tooltipPosition][1]}
          direction="down"
        >
          {multiElementResizeAvailable && (
            <div
              // eslint-disable-next-line yola/react/no-danger
              dangerouslySetInnerHTML={{
                __html: shiftPressed ? captions.shiftPressed : captions.shiftReleased,
              }}
            />
          )}
          <div
            // eslint-disable-next-line yola/react/no-danger
            dangerouslySetInnerHTML={{
              __html: commandPressed ? captions.cmdPressed : captions.cmdReleased,
            }}
          />
        </ResizeTooltip>
      )}
    </React.Fragment>
  );
};

ColumnResizeTool.propTypes = {
  elementId: PropTypes.string.isRequired,
  appearanceStyle: PropTypes.shape().isRequired,
  onActionStart: PropTypes.func.isRequired,
  onActionEnd: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

export default ColumnResizeTool;
